import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import * as graphql from '@treams/treams-network';
import {
	Button,
	Callout,
	Checkbox,
	Heading3,
	OrderedList,
	Paragraph,
	Separator,
} from '@treams/treams-ui';

export default function ProductSetup(props: {
	description: ReactNode;
	type: graphql.ConnectorType;
	translate: (key: string) => { title: string; description: string } | undefined;
	price?: number;
	available?: boolean;
	documentationLink?: string;
	onSetupCompleted: () => void;
}) {
	const [agreed, setAgreed] = useState(false);
	const [setup] = graphql.useSetupConnectorMutation();

	const { t } = useTranslation();

	return (
		<>
			<Flex>
				<Box width={1 / 2} mr={3}>
					<Heading3>Beschrijving van dit product</Heading3>
					<Separator mt={2} mb={3} />
					{props.description}
				</Box>
				<Box width={1 / 2}>
					<Heading3 mb={1}>Hoe verloopt het proces?</Heading3>
					<Separator mt={2} mb={3} />
					<Paragraph mb={2}>
						Het proces voor het inrichten van integraties verloopt volledig online. Je kunt het
						inrichten samen met een expert uit je team, met ervaring met het HR informatie- of
						salarissysteem (vanaf nu: HRIS). Bij vragen, kun je altijd een moment inplannen met een
						expert van Treams om ondersteund te worden, stuur een bericht in de chat of naar{' '}
						<a href="mailto:support@treams.nl">support@treams.nl</a>.
					</Paragraph>
					<Paragraph mb={2}>Vereiste tijd: ongeveer 2 uur</Paragraph>
					<Paragraph mb={2}>
						Te ondernemen acties:
						<OrderedList>
							<li>
								Je kunt Treams en het HRIS koppelen door in Treams de juiste toegangssleutels in te
								voeren en een paar simpele selecties te maken (bijv. welke organisaties uit het HRIS
								moeten worden ingeladen).
							</li>
							<li>Je selecteert de datum waarop de medewerkers toegang krijgen tot Treams.</li>
							<li>
								Je kunt eventuele uitsluitingen toevoegen, specifieke medewerkers of afdelingen die
								geen toegang mogen krijgen tot Treams.
							</li>
						</OrderedList>
						{props.documentationLink && (
							<Callout color="blue">
								<a href={props.documentationLink} target="_blank" rel="noreferrer noopener">
									{t('connectors.settings.configuration.callout')}
								</a>
							</Callout>
						)}
					</Paragraph>
				</Box>
			</Flex>

			{props.available ? (
				<>
					<Heading3 mb={1}>Voorwaarden</Heading3>
					<Separator mt={2} mb={3} />
					<Paragraph mb={4}>
						De prijs van deze integratie bedraagt €{props.price ? String(props.price) : '125'},- per
						maand. Deze integratie is maandelijks opzegbaar. Het wisselen van integratie met een HR
						informatie- of salarissysteem is mogelijk, maar dit betreft een proces en prijs op maat.
						Op alle integraties zijn de{' '}
						<a
							href="https://terms.ontreams.com/Algemene%20voorwaarden%20v1.0.pdf"
							target="_blank"
							rel="noopener noreferrer"
						>
							algemene voorwaarden
						</a>{' '}
						van toepassing. Treams onderhoudt de integratie met de derde partij en hier worden geen
						extra kosten voor in rekening gebracht. Het corrigeren van fouten of het oplossen van
						problemen bij de inrichting van de software van de derde partij valt niet binnen de
						prijs van de integratie, de gemaakte uren worden op basis van nacalculatie berekend.
						Treams zal dit ten allen tijden vooraf aangeven. Indien deze integratie niet naar wens
						werkt of ingericht kan worden, dient dit binnen 30 dagen kenbaar gemaakt te worden. Het
						volledig bedrag wordt dan gecrediteerd.
					</Paragraph>

					<Heading3 mb={1}>Aan de slag</Heading3>
					<Separator mt={2} mb={3} />
					<Flex mb={3} onClick={() => setAgreed((agreed) => !agreed)}>
						<Box flexGrow={0} flexShrink={0}>
							<Checkbox checked={agreed} />
						</Box>
						<Box flexGrow={1} flexShrink={1} ml={2}>
							<Paragraph>Ik ga akkoord met de voorwaarden voor deze connector.</Paragraph>
						</Box>
					</Flex>
					<Flex>
						<Button
							title={`${t(`connectors.product.${props.type.toLowerCase()}.title`)} ${t(
								'connectors.product.general.connect',
							)}`}
							disabled={!agreed}
							onClick={async () => {
								await setup({
									variables: { input: { type: props.type } },
								});
								props.onSetupCompleted();
							}}
						/>
					</Flex>
				</>
			) : (
				<>
					<Heading3 mb={1}>Aan de slag?</Heading3>
					<Separator mt={2} mb={3} />
					<Paragraph mb={4}>
						Je hebt op dit moment een andere HRIS integratie geselecteerd. Je kunt niet online
						wisselen van integratie.
						<br />
						Neem contact op via de chat of via support@treams.nl om te wisselen van integratie.
					</Paragraph>
				</>
			)}
		</>
	);
}

function ProductSetupSkeleton() {
	return (
		<>
			<Paragraph.Skeleton mb={3} />
			<Heading3.Skeleton mb={2} />
			<Paragraph.Skeleton mb={3} />
			<Heading3.Skeleton mb={2} />
			<Paragraph.Skeleton mb={3} />
			<Flex mb={3}>
				<Box flexGrow={0} flexShrink={0}>
					<Checkbox />
				</Box>
				<Box flexGrow={1} flexShrink={1} ml={2}>
					<Paragraph.Skeleton lines={1} />
				</Box>
			</Flex>
			<Flex>
				<Button.Skeleton />
			</Flex>
		</>
	);
}

ProductSetup.Skeleton = ProductSetupSkeleton;
